import isNil from 'lodash/isNil';
import max from 'lodash/max';
import min from 'lodash/min';
import omitBy from 'lodash/omitBy';

import { getImageSrcs } from 'shared/utils/getImageSrcs';
import { getProductTemplateCarouselImages } from 'productTemplate/utils/getProductTemplateCarouselImages';
import { IOffer } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { normalizeValue } from 'shared/utils/normalizeValue';

const isNilOrEmpty = (value) => isNil(value) || value === '' || value.length === 0;

const getOfferRange = (offers: IOffer[]) => {
  const prices = offers?.map((offer) => Number.parseFloat(offer.price));
  const high = max(prices) || 0;
  const low = min(prices) || 0;
  return { high, low };
};

const createVariantSchema = ({
  canonical,
  firstImage,
  offers,
  productTemplate,
}: {
  canonical: string;
  firstImage: string;
  offers: IOffer[];
  productTemplate: IProductTemplate;
}) => {
  const { sizeOptions, productCategory, name } = productTemplate;
  const productType = productCategory === 'shoes' ? 'sneakers' : 'apparel';

  return offers.map((offer) => {
    let sizeDisplay = offer.size?.toString();

    // we need to display whole sizes with a decimal (7 => 7.0)
    if (productType === 'sneakers') {
      sizeDisplay = offer.size?.toFixed(1);
    } else {
      // we need to show the presentation size for apparel
      const sizePresentation = sizeOptions.find((sizeOption) => sizeOption.value === offer.size);
      if (sizePresentation) {
        sizeDisplay = sizePresentation.presentation;
      }
    }

    return {
      '@type': 'Product',
      sku: offer?.gmcSku,
      name: `Size ${sizeDisplay} ${name}`,
      description: `${name} in Size ${sizeDisplay}`,
      url: `${canonical}?sizing=${sizeDisplay}`,
      image: firstImage,
      size: sizeDisplay,
      offers: {
        '@type': 'Offer',
        url: `${canonical}?sizing=${sizeDisplay}`,
        sku: offer?.gmcSku,
        priceCurrency: offer?.priceCurrency,
        price: offer?.price,
        size: sizeDisplay,
        itemCondition: `https://schema.org/${offer?.itemCondition}`,
        availability: offer?.availability,
      },
    };
  });
};

const getImageUrls = (imagesArrs) =>
  imagesArrs.map((image) => {
    const src = image.mainPictureUrl?.includes('/medium/')
      ? image?.mainPictureUrl?.replace('/medium/', '/original/')
      : image?.mainPictureUrl;

    return getImageSrcs({ src, resizeable: true })?.src;
  });

export const getProductGroupTemplateSchema = ({
  productTemplate,
  offers,
  canonical,
}: {
  productTemplate: IProductTemplate;
  offers: IOffer[];
  canonical: string;
}) => {
  const productTemplateImages = getProductTemplateCarouselImages({
    productTemplate,
    showRobots: true,
    robotsCutOff: 5,
    enablePdpImgAltTag: true,
  });
  const images = getImageUrls(productTemplateImages);
  const isOneSize = offers.length === 1 && offers?.[0]?.size === 200;
  const offerRange = getOfferRange(offers);
  const availability =
    offerRange.high > 0 && offerRange.low > 0
      ? 'http://schema.org/InStock'
      : 'http://schema.org/OutOfStock';

  if (availability === 'http://schema.org/OutOfStock') {
    return null;
  }

  return JSON.stringify(
    omitBy(
      {
        '@context': 'http://schema.org',
        '@type': isOneSize ? 'Product' : 'ProductGroup',
        name: productTemplate?.name,
        description: productTemplate?.story || null,
        url: canonical,
        brand: {
          '@type': 'Brand',
          name: productTemplate?.brandName,
        },
        sku: productTemplate?.sku,
        releaseDate: productTemplate?.releaseDate,
        color: productTemplate?.color || productTemplate?.details,
        category: normalizeValue(productTemplate.productType),
        image: images,
        offers: offers
          ? {
              '@type': 'AggregateOffer',
              availability,
              highPrice: Number(offerRange.high).toFixed(2),
              lowPrice: Number(offerRange.low).toFixed(2),
              priceCurrency: offers?.[0]?.priceCurrency || 'USD',
              offerCount: offers?.length,
            }
          : null,
        ...(!isOneSize && {
          productGroupID: productTemplate?.sku,
          variesBy: ['https://schema.org/size'],
          hasVariant: createVariantSchema({
            offers,
            productTemplate,
            firstImage: images?.[0],
            canonical,
          }),
        }),
      },
      isNilOrEmpty,
    ),
  );
};
